import {defineComponent, reactive, Ref, computed} from 'vue';
import {Validation, ErrorObject, useVuelidate} from '@vuelidate/core';
import {required} from '@vuelidate/validators';
import {serverSide} from '../../js/validation/validators';
import diContainer from '../../js/diContainer/diContainer';
import type {ValidationFieldConfigKey} from '../../js/validation/ValidationFieldConfig';

type ServerSideError = {fieldName: string, message: string};

const globalValidationInstances: Ref<Validation>[] = [];

export default defineComponent({
  name: 'ValidationMixin',
  _validator: null as Ref<Validation>|null,
  _serverSideErrors: [] as ServerSideError[],

  inject: {
    rootServerSideErrors: {
      default: () => []
    }
  },

  props: {
    validationChildName: {
      type: String,
      default: null,
    }
  },

  created() {
    const v$ = useVuelidate({
      $registerAs: this.validationChildName,
    }) as Ref<Validation>;
    globalValidationInstances.push(v$);

    this._validator = v$;
    this._serverSideErrors = reactive([]);
  },

  computed: {
    v$() {
      return this._validator.value;
    },

    serverSideErrors(): ServerSideError[] {
      return [...this._serverSideErrors, ...this.rootServerSideErrors];
    }
  },

  methods: {
    getValidationRules(fieldType: ValidationFieldConfigKey, isRequired = false, fieldNameForServerSide = '') {
      const validationFieldConfig = diContainer.get('ValidationFieldConfig');
      type ConfigKey = ValidationFieldConfigKey|'required'|'serverSide';
      const rules: Record<ConfigKey, unknown> = {...validationFieldConfig[fieldType]};
      if (!rules) {
        throw new Error(`ValidationMixin: Validation rules for ${fieldType} not found`);
      }

      if (fieldNameForServerSide) {
        rules.serverSide = serverSide(fieldNameForServerSide);
      }

      if (isRequired) {
        rules.required = required;
      }
      return rules;
    },

    createServerSideError(fieldName: string, message: string) {
      this._serverSideErrors.push({fieldName, message});
    },

    clearServerSideErrors() {
      this._serverSideErrors.splice(0, this._serverSideErrors.length);
    },

    validateAll() {
      this.v$.$validate();
    },

    getGlobalErrors(): ErrorObject[] {
      const errors = [];
      for (let i = 0; i < globalValidationInstances.length; i++) {
        if (globalValidationInstances[i].value.$errors.length > 0) {
          errors.push(...globalValidationInstances[i].value.$errors);
        }
      }
      return errors;
    },

    getErrorText(errors: ErrorObject[], customErrorPropertyName?: string): string {
      if (!errors || errors.length === 0) {
        return '';
      }
      const error = errors[0];

      if (error.$validator === 'serverSide' && error.$message === '') {
        const serverError = this.serverSideErrors.find((serverError: ServerSideError) => {
          return serverError.fieldName === error.$property || serverError.fieldName === error.$response?.param;
        });
        if (serverError) {
          return serverError.message;
        }
      }

      const propertyName = customErrorPropertyName ?? error.$property;

      const errorTextKeys = [
        `validation.custom.${propertyName}.${error.$validator}`,
        `validation.custom.${error.$validator}`,
        `validation.${propertyName}.${error.$validator}`,
        `validation.${error.$validator}`,
      ];

      let errorText: string;
      for (let i = 0; i < errorTextKeys.length; i++) {
        if (this.$te(errorTextKeys[i])) {
          const params: string[] = [error.$property, error.$params[error.$validator], error.$response?.param];
          errorText = this.$t(errorTextKeys[i], params);
          break;
        }
      }

      if (!errorText) {
        console.log(errorTextKeys.join('|')); // eslint-disable-line no-console
        errorText = this.$t('validation.messages._default');
      }
      return errorText;
    },

    provideServerSideErrors() {
      return {
        rootServerSideErrors: computed(() => this._serverSideErrors),
      };
    }
  },

  validations() {}

});
